import { DragDropProvider } from "@dnd-kit/react";
import { PropsWithChildren, ReactNode } from "react";
import { useSortableSafe } from "../../lib/dnd/dnd-kit/safe";
import { useSensors } from "../../lib/dnd/use-sensors";
import {
  CollisionMap,
  createDynamicCollisionDetector,
} from "../../lib/dnd/collision/dynamic";
import "./styles.css";

export const SortableProvider = ({
  children,
  onDragStart,
  onDragEnd,
  onMove,
}: PropsWithChildren<{
  onDragStart: (id: string) => void;
  onDragEnd: () => void;
  onMove: (moveData: { source: number; target: number }) => void;
}>) => {
  const sensors = useSensors({
    mouse: { distance: { value: 5 } },
  });

  return (
    <DragDropProvider
      sensors={sensors}
      onDragStart={(event) =>
        onDragStart(event.operation.source?.id.toString() ?? "")
      }
      onDragOver={(event, manager) => {
        event.preventDefault();

        const { source, target } = event.operation;

        if (!source || !target) return;

        const sourceIndex = (source as any)?.data?.index;
        let targetIndex = (target as any)?.data?.index;

        if (typeof sourceIndex !== "number" || typeof targetIndex !== "number") {
          return;
        }

        const collisionData = (
          (manager.dragOperation as any)?.data?.collisionMap as CollisionMap
        )?.[target.id];

        if (sourceIndex !== targetIndex && source.id !== target.id) {
          const collisionPosition =
            collisionData?.direction === "up" ? "before" : "after";

          if (targetIndex >= sourceIndex) {
            targetIndex = targetIndex - 1;
          }

          if (collisionPosition === "after") {
            targetIndex = targetIndex + 1;
          }

          onMove({
            source: sourceIndex,
            target: targetIndex,
          });
        }
      }}
      onDragEnd={() => {
        setTimeout(() => {
          onDragEnd();
        }, 250);
      }}
    >
      {children}
    </DragDropProvider>
  );
};

export const Sortable: React.FC<{
  id: string;
  index: number;
  disabled?: boolean;
  children: (props: {
    status: "idle" | "dragging" | "dropping";
    ref: (element: Element | null) => void;
    handleRef: (element: Element | null) => void;
  }) => React.ReactNode;
  type?: string;
}> = ({ id, index, disabled, children, type = "item" }) => {
  const sortable = useSortableSafe({
    id,
    type,
    index,
    disabled,
    data: { index },
    collisionDetector: createDynamicCollisionDetector("y"),
  });

  const status: "idle" | "dragging" | "dropping" =
    (sortable as any).status ?? "idle";

  const content = children({
    status,
    ref: sortable.ref,
    handleRef: sortable.handleRef,
  });

  // ✅ Return as valid JSX element
  return <>{content}</>;
};